import { Box, Grid2, Typography, Button, Skeleton } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import moment from "moment";
import { FC } from "react";

import { IVehicleLogRowDetails } from ".";
import { AGDialog } from "../../components/AGDialog";
import { IVehicleDetails } from "../../types";

interface IVahanDataDialogProps {
	open: boolean;
	vehicleLogDetails: IVehicleLogRowDetails | null;
	vehicleDetails?: IVehicleDetails;
	loading?: boolean;
	onClose: () => void;
	forceRefetchData: () => void;
}

const VahanDataDialog: FC<IVahanDataDialogProps> = (props) => {
	const { open, vehicleLogDetails, vehicleDetails, loading, onClose, forceRefetchData } = props;

	if (!vehicleLogDetails) return null;

	return (
		<AGDialog bgGrey open={open} onClose={onClose} title="Vahan Data" color="primary" maxWidth="lg">
			<Box sx={{ padding: "1rem 0" }}>
				<Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "1rem" }}>
					<Button
						variant="outlined"
						color="primary"
						startIcon={<RefreshIcon />}
						onClick={forceRefetchData}
						disabled={loading}
					>
						Refresh Data
					</Button>
				</Box>
				<Box sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
					{loading ? (
						<>
							{/* Registration Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={200} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									{[...Array(8)].map((_, index) => (
										<Grid2 key={index} size={{ xs: 12, sm: 6, md: 3 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Skeleton variant="text" width={120} height={20} />
												<Skeleton variant="text" width={150} height={24} />
											</Box>
										</Grid2>
									))}
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={140} height={20} />
											<Skeleton variant="text" width="100%" height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, md: 6 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={130} height={20} />
											<Skeleton variant="text" width="100%" height={24} />
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Vehicle Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={150} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									{[...Array(9)].map((_, index) => (
										<Grid2 key={index} size={{ xs: 12, sm: 6, md: 3 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Skeleton variant="text" width={100} height={20} />
												<Skeleton variant="text" width={120} height={24} />
											</Box>
										</Grid2>
									))}
								</Grid2>
							</Box>

							{/* Technical Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={160} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									{[...Array(4)].map((_, index) => (
										<Grid2 key={index} size={{ xs: 12, sm: 6, md: 3 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Skeleton variant="text" width={110} height={20} />
												<Skeleton variant="text" width={140} height={24} />
											</Box>
										</Grid2>
									))}
								</Grid2>
							</Box>

							{/* Insurance Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={160} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={120} height={20} />
											<Skeleton variant="text" width={80} height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={140} height={20} />
											<Skeleton variant="text" width="100%" height={24} />
										</Box>
									</Grid2>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Skeleton variant="text" width={160} height={20} />
											<Skeleton variant="text" width={100} height={24} />
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Enforcement Details Card Skeleton */}
							<Box className="event-log-details-card">
								<Skeleton variant="text" width={180} height={28} sx={{ marginBottom: "1rem" }} />
								<Grid2 container spacing={2}>
									{[...Array(8)].map((_, index) => (
										<Grid2 key={index} size={{ xs: 12, sm: 6, md: 3 }}>
											<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
												<Skeleton variant="text" width={100} height={20} />
												<Skeleton variant="text" width={120} height={24} />
											</Box>
										</Grid2>
									))}
								</Grid2>
							</Box>
						</>
					) : (
						<>
							{/* Registration Details Card */}
							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Registration Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Registration Number
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.number_plate || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Registration Date
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.registration_date
													? moment(vehicleDetails.registration_date).format("DD/MM/YYYY")
													: "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Registration Authority
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.registered_at || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Registration Valid Upto
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.registration_valid_upto
													? moment(vehicleDetails.registration_valid_upto).format("DD/MM/YYYY")
													: "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Owner Name
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.owner_name ?? "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Owner Mobile Number
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.rc_mobile_no ?? "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 6 }} display={{ xs: "none", sm: "block" }} />

									<Grid2 size={{ xs: 12, md: 6 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Permanent Address
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.permanent_address ?? "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, md: 6 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Current Address
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.present_address ?? "--"}
											</Typography>
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Vehicle Details Card */}
							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Vehicle Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Vehicle Status
											</Typography>

											<Typography
												variant="body1"
												fontWeight={500}
												color={vehicleDetails?.vehicle_status?.toLowerCase() === "active" ? "success" : "textPrimary"}
											>
												{vehicleDetails?.vehicle_status || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												PUCC Number
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.pucc_number || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												PUCC Valid Upto
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.pucc_valid_upto
													? moment(vehicleDetails.pucc_valid_upto).format("DD/MM/YYYY")
													: "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Fitness Valid Upto
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.fitness_valid_upto
													? moment(vehicleDetails.fitness_valid_upto).format("DD/MM/YYYY")
													: "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Vehicle Class
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.vehicle_class ||
													vehicleLogDetails.vehicle_class ||
													vehicleLogDetails.class ||
													"--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Make/Brand
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.maker_description || vehicleLogDetails.brand || vehicleLogDetails.make || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Model
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.maker_model || vehicleLogDetails.model || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Color
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.color || vehicleLogDetails.color || "--"}
											</Typography>
										</Box>
									</Grid2>

									{/* <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Category
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.category || "--"}
											</Typography>
										</Box>
									</Grid2> */}

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Blacklist Status
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.blacklist_status || "N/A"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												NCRB Status
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.ncrb_status || "--"}
											</Typography>
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Technical Details Card */}
							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Technical Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Chassis Number
											</Typography>

											<Typography variant="body1" fontWeight={500} sx={{ wordBreak: "break-all" }}>
												{vehicleDetails?.chassis_number || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Engine Number
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.engine_number || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Fuel Type
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.fuel_type || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Seating Capacity
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.seating_capacity || "--"}
											</Typography>
										</Box>
									</Grid2>

									{/* <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
									<Typography variant="body2" color="text.secondary">
										Engine Capacity
									</Typography>

									<Typography variant="body1" fontWeight={500}>
										1197 CC
									</Typography>
								</Box>
							</Grid2> */}

									{/* <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
									<Typography variant="body2" color="text.secondary">
										Manufacturing Year
									</Typography>

									<Typography variant="body1" fontWeight={500}>
										2020
									</Typography>
								</Box>
							</Grid2> */}
								</Grid2>
							</Box>

							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Insurance Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Insurance Status
											</Typography>

											<Typography
												variant="body1"
												fontWeight={500}
												color={
													typeof vehicleDetails?.is_insurance_valid === "boolean"
														? vehicleDetails?.is_insurance_valid
															? "success"
															: "error"
														: "textPrimary"
												}
											>
												{typeof vehicleDetails?.is_insurance_valid === "boolean"
													? vehicleDetails?.is_insurance_valid
														? "Valid"
														: "Expired"
													: "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 6 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Insurance Company
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.insurance_company || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Insurance Valid Upto
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleDetails?.insurance_valid_upto
													? moment(vehicleDetails.insurance_valid_upto).format("DD/MM/YYYY")
													: "--"}
											</Typography>
										</Box>
									</Grid2>
								</Grid2>
							</Box>

							{/* Enforcement Details Card */}
							<Box className="event-log-details-card">
								<Typography variant="h6" fontWeight={600} color="primary" sx={{ marginBottom: "1rem" }}>
									Enforcement Details
								</Typography>

								<Grid2 container spacing={2}>
									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Detection Time
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.timestamp
													? moment(vehicleLogDetails.timestamp).format("DD/MM/YYYY HH:mm:ss")
													: "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												District
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.location_name || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Officer
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.officer || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Device
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.device || "--"}
											</Typography>
										</Box>
									</Grid2>

									{/* <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Speed (Vision)
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.speed || vehicleLogDetails.speed_vision || "--"}
											</Typography>
										</Box>
									</Grid2> */}

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Speed (Radar)
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.radar_speed || vehicleLogDetails.speed_radar || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Direction
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.direction_name || "--"}
											</Typography>
										</Box>
									</Grid2>

									<Grid2 size={{ xs: 12, md: 6 }}>
										<Box sx={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
											<Typography variant="body2" color="text.secondary">
												Violation Type
											</Typography>

											<Typography variant="body1" fontWeight={500}>
												{vehicleLogDetails.violations?.length > 0
													? vehicleLogDetails.violations.map((item) => item.name).join(", ")
													: "--"}
											</Typography>
										</Box>
									</Grid2>
								</Grid2>
							</Box>
						</>
					)}
				</Box>
			</Box>
		</AGDialog>
	);
};

export default VahanDataDialog;
